import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, CalendarCheck, Calendar, MapPin, Clock, Trash2, ThumbsUp, ThumbsDown, Paperclip } from 'lucide-react';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const AppointmentsTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('appointment');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this appointment? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  const getGoBackBadge = (choice) => {
    if (choice === 'yes') return <Badge className="bg-green-100 text-green-700"><ThumbsUp className="h-3 w-3 mr-1" />Would go back</Badge>;
    if (choice === 'no') return <Badge className="bg-red-100 text-red-700"><ThumbsDown className="h-3 w-3 mr-1" />Would not go back</Badge>;
    if (choice === 'maybe') return <Badge className="bg-yellow-100 text-yellow-700">Maybe</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Appointments</h3>
          <p className="text-sm text-gray-500">Time-specific appointments with experience tracking</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-appointment-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Appointment
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <CalendarCheck className="h-12 w-12 mx-auto text-[#C5D9ED] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Track Your Appointments</h4>
          <p className="text-gray-600 mb-4">
            Add appointments and record your experiences for future reference
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Appointment
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => (
            <Card key={item.id} className={`border-l-4 ${item.would_go_back === 'yes' ? 'border-l-green-400' : 'border-l-[#C5D9ED]'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      {item.would_go_back && getGoBackBadge(item.would_go_back)}
                      {item.attachments && item.attachments.length > 0 && (
                        <Badge variant="outline">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {item.attachments.length} files
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </span>
                        {item.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {item.time}
                            {item.duration_minutes && ` (${item.duration_minutes} min)`}
                          </span>
                        )}
                      </div>
                      
                      {item.provider_office && (
                        <p className="text-gray-700">{item.provider_office}</p>
                      )}
                      
                      {item.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {item.location}
                        </span>
                      )}
                      
                      {item.experience_notes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded border">
                          <p className="text-gray-700 italic">{item.experience_notes}</p>
                        </div>
                      )}
                      
                      {item.notes && (
                        <p className="text-gray-500 italic mt-1">{item.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <AddToCalendarButton
                      dateRecord={{
                        title: item.title,
                        date: item.date,
                        time: item.time,
                        location: item.location || item.provider_office || '',
                        type: 'appointment',
                        all_day: !item.time,
                        reminder_minutes: 60, // 1 hour before
                        notes: item.notes || ''
                      }}
                      size="sm"
                      variant="ghost"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      data-testid={`add-to-calendar-${item.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="appointment"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default AppointmentsTab;
